/**
 * THE TOAST (0.1.455) — a small bubble at the bottom right, LEFT of the screen's floating pill,
 * gone after two seconds. Maintainer, 3 Sep 2026: "the update message that now shows up above
 * the display row should become a small text bubble at the right bottom of my screen that pops
 * up for 2 seconds to the left of the + New box action pill."
 *
 * Until 0.1.455 the three list screens (boxes / items / outfits) each rendered their status line
 * as a `<p role="status">` in the flow above the sheet — it pushed the Display bar down and
 * stayed until the next act. This is the same message in the frame's own idiom: it rides the
 * `.float-stack` (rule 4), so it is always beside whatever pill is there, at any width, and it
 * clears the gesture area through the same `--float-bottom`.
 *
 * STILL `role="status"` — assistive tech announces it exactly as the `<p>` was announced; only
 * where it paints and for how long changed. The timer is keyed on the MESSAGE IDENTITY (the
 * caller's `key`), so the same sentence twice in a row restarts the two seconds rather than
 * vanishing early. Hover pauses nothing on purpose: a bubble that must be read is a banner.
 *
 * The caller places it INSIDE its `.float-stack` — or in an empty one when no pill floats.
 */
import { useCallback, useEffect, useState } from 'react';

export const TOAST_MS = 2000;

export interface ToastMessage {
  /** Identity of THIS showing — a counter the caller bumps on every set. */
  key: number;
  text: string;
}

export function Toast({ message, onDone }: { message: ToastMessage; onDone: () => void }): JSX.Element {
  useEffect(() => {
    const handle = setTimeout(onDone, TOAST_MS);
    return () => clearTimeout(handle);
    // Re-armed per showing, not per text: message.key is the identity.
  }, [message.key]);
  return (
    <div className="float-toast" role="status">
      {message.text}
    </div>
  );
}

/**
 * A screen's status setter, toast-shaped: `const [toast, setToast] = useToast()` gives the same
 * `(line: string | null) => void` the old `setStatusLine` was, so every call site stays as it
 * is — only the render changes.
 */
export function useToast(): [ToastMessage | null, (line: string | null) => void] {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const set = useCallback((line: string | null) => {
    if (line === null) setMessage(null);
    else setMessage((prev) => ({ key: (prev?.key ?? 0) + 1, text: line }));
  }, []);
  return [message, set];
}
